import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { QUOTE_TYPES } from "@/lib/dseQuotes";
import DseModelPicker from "@/components/dse/DseModelPicker";
import DseEmiSection from "@/components/dse/DseEmiSection";
import DseQuoteResult from "@/components/dse/DseQuoteResult";

const EMPTY_EMI = { enabled: false, scheme_id: "", down_payment: "" };

function Toggle({ label, on, onChange, testId }) {
  return (
    <button type="button" onClick={() => onChange(!on)} data-testid={testId} data-active={on ? "true" : "false"}
      className={`flex items-center justify-between w-full px-3 py-3 border text-sm transition-colors ${on ? "border-ink bg-ink text-white" : "border-canvas-line bg-white text-ink"}`}>
      {label}
      <span className={`font-mono text-[10px] tracking-widest ${on ? "text-amber-300" : "text-ink-muted"}`}>{on ? "YES" : "NO"}</span>
    </button>
  );
}

export default function DseQuoteForm({ type, schemes }) {
  const cfg = QUOTE_TYPES[type];
  const [rows, setRows] = useState([]);
  const [row, setRow] = useState(null);
  const [opts, setOpts] = useState(cfg.defaults);
  const [emi, setEmi] = useState({ ...EMPTY_EMI, enabled: !cfg.emiOptional });
  const [customer, setCustomer] = useState({ name: "", phone: "" });
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);

  useEffect(() => {
    setRow(null); setOpts(cfg.defaults); setEmi({ ...EMPTY_EMI, enabled: !cfg.emiOptional }); setCreated(null);
    api.get(cfg.pricelist).then(({ data }) => setRows(data || [])).catch((e) => toast.error(formatApiError(e)));
  }, [type]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    if (!row) return toast.error("Pick a model");
    if (!customer.name.trim()) return toast.error("Customer name is required");
    if (!/^\d{10}$/.test(customer.phone.trim())) return toast.error("Enter a 10-digit mobile number");
    if (emi.enabled && !emi.scheme_id) return toast.error("Pick a finance scheme");
    setSaving(true);
    try {
      const { data } = await api.post(cfg.base, cfg.payload(row, opts, { name: customer.name.trim(), phone: customer.phone.trim() }, emi));
      setCreated(data);
      toast.success(`Quote ${data.serial} created`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const reset = () => { setCreated(null); setRow(null); setCustomer({ name: "", phone: "" }); setEmi({ ...EMPTY_EMI, enabled: !cfg.emiOptional }); };

  if (created) return <DseQuoteResult type={type} quote={created} onNew={reset} />;

  const label = "block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-2";
  return (
    <div className="space-y-6" data-testid="dse-quote-form">
      <section>
        <span className={label}>1 · Model</span>
        <DseModelPicker rows={rows} value={row} onChange={setRow} />
      </section>

      <section className="space-y-2">
        <span className={label}>2 · Options</span>
        {cfg.options.map((o) => (
          <Toggle key={o.key} label={o.label} on={!!opts[o.key]} testId={`dse-opt-${o.key}`}
            onChange={(v) => setOpts((p) => ({ ...p, [o.key]: v }))} />
        ))}
      </section>

      <DseEmiSection schemes={schemes} emi={emi} setEmi={setEmi} optional={cfg.emiOptional} labelCls={label} />

      <section className="space-y-2">
        <span className={label}>4 · Customer</span>
        <input value={customer.name} onChange={(e) => setCustomer((c) => ({ ...c, name: e.target.value }))}
          placeholder="Customer name" data-testid="dse-customer-name"
          className="w-full border border-canvas-line bg-white px-3 py-3 text-sm focus:outline-none focus:border-ink" />
        <input value={customer.phone} inputMode="numeric" maxLength={10}
          onChange={(e) => setCustomer((c) => ({ ...c, phone: e.target.value.replace(/\D/g, "") }))}
          placeholder="WhatsApp mobile (10 digits)" data-testid="dse-customer-phone"
          className="w-full border border-canvas-line bg-white px-3 py-3 font-mono text-sm focus:outline-none focus:border-ink" />
      </section>

      <button type="button" onClick={submit} disabled={saving} data-testid="dse-create-quote"
        className="w-full py-4 bg-ink text-white font-semibold tracking-wide transition-colors hover:bg-ink/90 active:scale-[0.99] disabled:opacity-60">
        {saving ? "Creating quote…" : "Create quote"}
      </button>
      <p className="text-[11px] text-ink-muted text-center">Quotes use today's list price. Discounts are not available in this app.</p>
    </div>
  );
}
